// src/components/AvatarDialog.jsx
// Profile picture (3.22.2): upload a photo, pick one of the built-in icons,
// or go back to their initials. A photo is cropped to a 128×128 circle here
// in the browser; only that small copy is uploaded (the original never
// leaves their computer). Same look as My profile (its dialog styles).
// 3.44.1: a photo opens a cropper first (drag to move, slider or wheel to
// zoom); 48 icons in three groups; the chosen icon is marked in the theme's
// colours (palette variables).
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotify } from './Notifications';
import { saveAvatar } from '../utils/api';
import Avatar, { initialsOf } from './Avatar';
import { dialogStyles as S } from './ProfileDialog';
import Emo from './Emo';

// Keep in step with AVATAR_ICONS in the backend (routes/portal.js), which
// refuses any icon not on its list.
export const AVATAR_GROUPS = [
  ['Animals', ['🦊', '🐼', '🐨', '🦁', '🐯', '🐸', '🐙', '🦉', '🐢', '🦄', '🐬', '🦋', '🐝', '🐧', '🐳', '🦜', '🐞', '🐶', '🐱', '🐰', '🐻', '🦒', '🦩']],
  ['Nature', ['🌻', '🌊', '⭐', '🌈', '🌸', '🍀', '🌙', '☀️', '🌵', '🌴', '🍁']],
  ['Fun and things', ['🚀', '🎧', '📚', '☕', '🔥', '💎', '🎨', '🎯', '🎵', '🏆', '🧩', '🪴', '💡', '🩺']],
];
export const AVATAR_ICONS = AVATAR_GROUPS.flatMap(([, list]) => list);
const MAX_FILE = 10 * 1024 * 1024;   // bigger photos are refused before cropping
const SIZE = 128, MAX_BYTES = 38 * 1024;
const FRAME = 240;                   // the cropper's square, in screen pixels
const MAX_ZOOM = 4;

// Load a photo file so the cropper can show it
function loadPhoto(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ img, url, w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('That file could not be read as a photo.')); };
    img.src = url;
  });
}

// The part of the photo inside the circle → a 128×128 data URL, small enough to save
function cropToDataUrl(photo, zoom, off) {
  const scale = Math.max(FRAME / photo.w, FRAME / photo.h) * zoom;
  const dispW = photo.w * scale, dispH = photo.h * scale;
  const sx = ((dispW - FRAME) / 2 - off.x) / scale;
  const sy = ((dispH - FRAME) / 2 - off.y) / scale;
  const side = FRAME / scale;
  const canvas = document.createElement('canvas');
  canvas.width = SIZE; canvas.height = SIZE;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, SIZE, SIZE);
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(photo.img, sx, sy, side, side, 0, 0, SIZE, SIZE);
  for (const q of [0.88, 0.75, 0.6, 0.45]) {
    const out = canvas.toDataURL('image/jpeg', q);
    if (out.length * 0.75 <= MAX_BYTES) return out;
  }
  throw new Error('That photo could not be made small enough. Please try another one.');
}

// Keep the photo covering the whole circle
function clamp(photo, zoom, off) {
  const scale = Math.max(FRAME / photo.w, FRAME / photo.h) * zoom;
  const mx = (photo.w * scale - FRAME) / 2, my = (photo.h * scale - FRAME) / 2;
  return { x: Math.max(-mx, Math.min(mx, off.x)), y: Math.max(-my, Math.min(my, off.y)) };
}

function Cropper({ photo, onUse, onCancel, onAnother }) {
  const [zoom, setZoom] = useState(1);
  const [off, setOff] = useState({ x: 0, y: 0 });
  const [err, setErr] = useState('');
  const frameRef = useRef(null), drag = useRef(null);
  const zoomRef = useRef(zoom); zoomRef.current = zoom;

  const setZoomTo = z => {
    const nz = Math.max(1, Math.min(MAX_ZOOM, z));
    setOff(o => clamp(photo, nz, { x: o.x * nz / zoomRef.current, y: o.y * nz / zoomRef.current }));
    setZoom(nz);
  };

  // Wheel / trackpad zoom (a native listener, so the page doesn't scroll)
  useEffect(() => {
    const el = frameRef.current;
    if (!el) return undefined;
    const onWheel = e => { e.preventDefault(); setZoomTo(zoomRef.current * (e.deltaY < 0 ? 1.08 : 1 / 1.08)); };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  });   // eslint-disable-line react-hooks/exhaustive-deps

  const scale = Math.max(FRAME / photo.w, FRAME / photo.h) * zoom;
  const dispW = photo.w * scale, dispH = photo.h * scale;

  function onDown(e) {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, off };
  }
  function onMove(e) {
    if (!drag.current) return;
    const d = drag.current;
    setOff(clamp(photo, zoom, { x: d.off.x + e.clientX - d.x, y: d.off.y + e.clientY - d.y }));
  }
  function onKey(e) {
    const step = e.shiftKey ? 20 : 6;
    const moves = { ArrowLeft: [step, 0], ArrowRight: [-step, 0], ArrowUp: [0, step], ArrowDown: [0, -step] };
    if (moves[e.key]) { e.preventDefault(); setOff(o => clamp(photo, zoom, { x: o.x + moves[e.key][0], y: o.y + moves[e.key][1] })); }
    else if (e.key === '+' || e.key === '=') { e.preventDefault(); setZoomTo(zoom * 1.1); }
    else if (e.key === '-') { e.preventDefault(); setZoomTo(zoom / 1.1); }
  }
  function use() {
    try { onUse(cropToDataUrl(photo, zoom, off)); } catch (x) { setErr(x.message); }
  }

  return (
    <section style={S.section} aria-label="Crop your photo">
      <h3 style={S.sectionTitle}>Fit your photo in the circle</h3>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
        <div ref={frameRef} className="av-crop-frame" tabIndex={0} role="application"
          aria-label="Photo position. Drag, or use the arrow keys to move it; plus and minus to zoom."
          onPointerDown={onDown} onPointerMove={onMove} onPointerUp={() => { drag.current = null; }} onPointerCancel={() => { drag.current = null; }}
          onKeyDown={onKey}
          style={{ position: 'relative', width: FRAME, height: FRAME, borderRadius: 14, overflow: 'hidden', cursor: drag.current ? 'grabbing' : 'grab', touchAction: 'none', background: 'var(--c-263238)', userSelect: 'none' }}>
          <img src={photo.url} alt="" draggable={false}
            style={{ position: 'absolute', left: FRAME / 2 - dispW / 2 + off.x, top: FRAME / 2 - dispH / 2 + off.y, width: dispW, height: dispH, maxWidth: 'none', pointerEvents: 'none' }} />
          <div aria-hidden="true" className="av-crop-ring" style={{ position: 'absolute', inset: 0, borderRadius: '50%', boxShadow: '0 0 0 999px rgb(var(--c-000000-rgb) / 0.5)', pointerEvents: 'none' }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: FRAME + 40 }}>
          <button type="button" onClick={() => setZoomTo(zoom / 1.2)} style={S.secondaryBtn} aria-label="Zoom out">−</button>
          <input type="range" min={1} max={MAX_ZOOM} step={0.01} value={zoom} onChange={e => setZoomTo(Number(e.target.value))}
            aria-label="Zoom" className="av-zoom" style={{ flex: 1 }} />
          <button type="button" onClick={() => setZoomTo(zoom * 1.2)} style={S.secondaryBtn} aria-label="Zoom in">+</button>
        </div>
        <p style={{ ...S.note, margin: 0, textAlign: 'center' }}>Drag the photo to move it; use the slider or your mouse wheel to zoom.</p>
        {err && <div role="alert" style={{ color: 'var(--t-b71c1c)', fontSize: 13 }}>{err}</div>}
        <div style={{ ...S.row, justifyContent: 'center' }}>
          <button type="button" onClick={onCancel} style={S.secondaryBtn}>Back</button>
          <button type="button" onClick={onAnother} style={S.secondaryBtn}><Emo e="📷" /> Another photo…</button>
          <button type="button" onClick={use} style={{ ...S.secondaryBtn, background: 'var(--c-00897b)', color: 'var(--t-ffffff)', borderColor: 'var(--c-00897b)' }}>Use this photo</button>
        </div>
      </div>
    </section>
  );
}

export default function AvatarDialog({ onClose }) {
  const { userInfo, user, getToken, updateUserInfo } = useAuth();
  const { notify } = useNotify();
  const [pick, setPick] = useState(userInfo?.avatar || null);
  const [photo, setPhoto] = useState(null);   // a photo being cropped
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef(null), closeRef = useRef(null);
  const name = userInfo?.name || user?.email;

  // 3.57.5: once, when the window opens (the parent passes a new onClose on
  // every render; re-running moved the cursor to ✕ on every refresh, e.g. a
  // fax pop-up, and the next Space/Enter closed the window)
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = e => { if (e.key === 'Escape') { e.preventDefault(); onCloseRef.current(); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Let the browser free the photo when the cropper closes
  useEffect(() => () => { if (photo) URL.revokeObjectURL(photo.url); }, [photo]);

  async function onFile(e) {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setErr('');
    if (!/^image\//.test(f.type)) { setErr('Please choose a photo (JPG or PNG).'); return; }
    if (f.size > MAX_FILE) { setErr('That photo is larger than 10 MB. Please choose a smaller one.'); return; }
    try { setPhoto(await loadPhoto(f)); } catch (x) { setErr(x.message); }
  }

  async function save() {
    setBusy(true); setErr('');
    try {
      const r = await saveAvatar(pick, getToken);
      if (r.error) throw new Error(r.error);
      updateUserInfo({ avatar: r.avatar || null });
      notify({ tone: 'success', title: 'Profile picture saved' });
      onClose();
    } catch (x) { setErr(x.message); }
    setBusy(false);
  }

  return (
    <div style={S.overlay} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <style>{CSS}</style>
      <div style={{ ...S.modal, maxWidth: 480 }} role="dialog" aria-modal="true" aria-labelledby="avatar-title">
        <div style={S.header} className="dlg-head">
          <div style={S.who}>
            <Avatar avatar={pick} name={name} size={42} />
            <div style={{ minWidth: 0 }}>
              <h2 id="avatar-title" style={S.title}><Emo e="🖼️" /> Profile picture</h2>
              <div style={S.sub}>Shown next to your name in the portal</div>
            </div>
          </div>
          <button ref={closeRef} onClick={onClose} style={S.closeBtn} aria-label="Close">✕</button>
        </div>
        <div style={S.body}>
          <input ref={fileRef} type="file" accept="image/*" onChange={onFile} style={{ display: 'none' }} />
          {photo ? (
            <Cropper key={photo.url} photo={photo} onCancel={() => setPhoto(null)} onAnother={() => fileRef.current?.click()}
              onUse={image => { setPick({ image }); setPhoto(null); }} />
          ) : (
            <>
              <section style={S.section}>
                <h3 style={S.sectionTitle}>Your photo</h3>
                <div style={S.row}>
                  <button onClick={() => fileRef.current?.click()} style={S.secondaryBtn}><Emo e="📷" /> Upload a photo…</button>
                </div>
                <p style={S.note}>You can move and zoom it to fit the circle. It's made small (128 × 128) on your computer before it's saved; the original photo isn't uploaded.</p>
              </section>
              <section style={S.section}>
                <h3 style={S.sectionTitle}>Or pick an icon</h3>
                <div style={{ maxHeight: 236, overflowY: 'auto', paddingRight: 4 }}>
                  {AVATAR_GROUPS.map(([group, list]) => (
                    <div key={group} style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--t-607d8b)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '2px 0 6px' }}>{group}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {list.map(i => (
                          <button key={i} type="button" className={'av-ico' + (pick?.icon === i ? ' on' : '')} onClick={() => setPick({ icon: i })}
                            aria-label={`Icon ${i}`} aria-pressed={pick?.icon === i}>
                            <span>{i}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
              <section style={S.section}>
                <button onClick={() => setPick(null)} style={{ ...S.secondaryBtn, display: 'inline-flex', alignItems: 'center', gap: 8 }} disabled={!pick}>
                  <Avatar name={name} size={22} /> Use my initials ({initialsOf(name)})
                </button>
              </section>
              {err && <div role="alert" style={{ color: 'var(--t-b71c1c)', fontSize: 13, margin: '6px 0' }}>{err}</div>}
              <div style={{ ...S.row, justifyContent: 'flex-end', marginTop: 12 }}>
                <button onClick={onClose} style={S.secondaryBtn}>Cancel</button>
                <button onClick={save} disabled={busy} style={{ ...S.secondaryBtn, background: 'var(--c-00897b)', color: 'var(--t-ffffff)', borderColor: 'var(--c-00897b)' }}>{busy ? 'Saving…' : 'Save'}</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const CSS = `
.av-ico { width: 42px; height: 42px; border-radius: 50%; font-size: 22px; line-height: 1; cursor: pointer; padding: 0;
  display: inline-grid; place-items: center; position: relative;
  background: var(--c-f5faf9); border: 1.5px solid var(--c-e0f2f1);
  transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease, background .18s ease; }
.av-ico span { display: block; transition: transform .25s cubic-bezier(.3,1.6,.5,1); }
.av-ico:hover { transform: scale(1.08); border-color: var(--c-80cbc4); box-shadow: 0 4px 10px rgb(var(--c-00897b-rgb) / 0.18); }
.av-ico:hover span { transform: scale(1.15) rotate(-6deg); }
.av-ico:focus-visible { outline: 2px solid var(--c-00897b); outline-offset: 2px; }
.av-ico.on { background: linear-gradient(135deg, var(--c-b2dfdb), var(--c-e0f2f1) 55%, var(--b-c8e6c9));
  border: 2px solid transparent;
  box-shadow: 0 0 0 2px var(--c-00897b), 0 4px 12px rgb(var(--c-00897b-rgb) / 0.28); }
.av-ico.on::after { content: ''; position: absolute; inset: -5px; border-radius: 50%; border: 2px solid rgb(var(--c-26a69a-rgb) / 0.5);
  animation: avPulse 1.8s ease-out infinite; pointer-events: none; }
.av-ico.on span { transform: scale(1.1); }
@keyframes avPulse { 0% { transform: scale(.92); opacity: .9; } 100% { transform: scale(1.18); opacity: 0; } }
.av-crop-frame:focus-visible { outline: 3px solid var(--c-00897b); outline-offset: 3px; }
.av-crop-ring::after { content: ''; position: absolute; inset: 0; border-radius: 50%; border: 2px solid rgb(var(--c-ffffff-rgb) / 0.9); }
.av-zoom { accent-color: var(--c-00897b); }
`;
